import { useState } from 'react';
import { Anchor, Badge, Button, Group, Modal, Paper, Stack, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { IconExternalLink } from '@tabler/icons-react';
import {
  SUPPORT_EMAIL,
  TEST_OFFER_BADGE,
  formatDaysLeft,
  type AccessDenialReason,
  type AccessInfo,
  type AccessState,
  type CheckoutProduct,
  type SellableContentKind,
} from '@creativitas/shared';
import { OFFER_KEY, fetchOffer } from '@/features/admin/payments-api';
import { useSession } from '@/features/auth/useSession';
import { CheckoutProducts } from '@/features/payments/CheckoutProducts';

/**
 * Кнопки «Оформить», «Продлить», «Перейти на Про» (docs/PAYMENTS.md, §10).
 *
 * При прямой оплате (docs/payments/ROBOKASSA_DIRECT.md) открывают окно оплаты
 * здесь же. Иначе ведут на страницу тарифов на Tilda — адрес задаёт администратор. Пока приём
 * оплат в тестовом режиме, кнопки ведут на тестовое оформление и помечены как
 * тестовая покупка (решение 15.10.2026): обычный путь покупателя должен
 * проходиться целиком, но настоящих списаний до запуска продаж быть не должно.
 *
 * При действующем Про кнопки Лайт нет. Переход с Лайт на Про сначала
 * предупреждает: оставшиеся дни Лайт сгорят.
 */

export function useOffer() {
  return useQuery({ queryKey: OFFER_KEY, queryFn: fetchOffer, staleTime: 60_000, retry: false });
}

type Subscription = AccessState['subscription'];

/** Какие товары показать в окне оплаты. */
type CheckoutFilter = (product: CheckoutProduct) => boolean;

const plansOnly = (plan?: 'light' | 'pro'): CheckoutFilter => (product) =>
  product.grantType === 'plan' && (plan === undefined || product.plan === plan);

/**
 * Окно прямой оплаты (docs/payments/ROBOKASSA_DIRECT.md): товары с ценой и
 * тем, что даст покупка, кнопка «Оплатить» — счёт и переход в Robokassa.
 */
function CheckoutModal({ filter, onClose }: { filter: CheckoutFilter | null; onClose: () => void }) {
  return (
    <Modal opened={filter !== null} onClose={onClose} title="Оплата" size="xl">
      {filter ? <CheckoutProducts filter={filter} /> : null}
    </Modal>
  );
}

export function PlanOfferButtons({ subscription, reason }: { subscription: Subscription; reason?: AccessDenialReason }) {
  const offer = useOffer();
  const [confirmUpgrade, setConfirmUpgrade] = useState(false);
  const [checkout, setCheckout] = useState<CheckoutFilter | null>(null);
  const url = offer.data?.visible ? offer.data.url : null;
  if (!url) return null;
  const direct = offer.data?.direct ?? false;

  /* При прямой оплате — окно с товарами здесь же; иначе — страница тарифов Tilda. */
  const open = (filter: CheckoutFilter) => () => {
    if (direct) setCheckout(() => filter);
    else window.open(url, '_blank', 'noopener');
  };
  const plan = subscription.active ? subscription.plan : null;

  const buttons: { label: string; onClick: () => void; variant: 'filled' | 'light' }[] = [];
  if (plan === 'pro') {
    if (!reason) buttons.push({ label: 'Продлить Про', onClick: open(plansOnly('pro')), variant: 'light' });
  } else if (plan === 'light') {
    if (!reason) buttons.push({ label: 'Продлить Лайт', onClick: open(plansOnly('light')), variant: 'light' });
    buttons.push({
      label: 'Перейти на Про',
      // В окне прямой оплаты предупреждение о сгорающем Лайт стоит у каждого Про.
      onClick: direct ? open(plansOnly('pro')) : () => setConfirmUpgrade(true),
      variant: reason ? 'filled' : 'light',
    });
  } else {
    buttons.push({
      label: reason === 'pro_required' ? 'Оформить Про' : 'Оформить тариф',
      onClick: open(plansOnly(reason === 'pro_required' ? 'pro' : undefined)),
      variant: 'filled',
    });
  }
  if (buttons.length === 0) return null;
  const icon = direct ? undefined : <IconExternalLink size={13} />;

  return (
    <>
      <Group gap="xs" wrap="wrap" data-testid="plan-offer">
        {buttons.map((button) => (
          <Button key={button.label} size="xs" variant={button.variant} rightSection={icon} onClick={button.onClick}>
            {button.label}
          </Button>
        ))}
        {offer.data?.test ? (
          <Badge size="xs" variant="light" color="grape">{TEST_OFFER_BADGE}</Badge>
        ) : null}
      </Group>
      <CheckoutModal filter={checkout} onClose={() => setCheckout(null)} />
      <Modal opened={confirmUpgrade} onClose={() => setConfirmUpgrade(false)} title="Переход на Про" size="sm">
        <Stack gap="sm">
          <Text size="sm">
            Про начнёт действовать с оплаты на срок выбранного предложения. Тариф Лайт прекратится в момент перехода
            {subscription.daysLeft ? `: оставшиеся ${formatDaysLeft(subscription.daysLeft)} не переносятся и не возвращаются` : ''}.
          </Text>
          <Group justify="flex-end">
            <Button size="xs" variant="subtle" onClick={() => setConfirmUpgrade(false)}>Отмена</Button>
            <Button
              size="xs"
              rightSection={<IconExternalLink size={13} />}
              onClick={() => {
                setConfirmUpgrade(false);
                window.open(url, '_blank', 'noopener');
              }}
            >
              Понятно, перейти к оплате
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

type Denied = Extract<AccessInfo, { allowed: false }>;

/** Каким тарифом открывается материал — по его правилам, а не по одной причине отказа. */
function tariffRoute(rules: Denied['declaredRules']): 'light_required' | 'pro_required' | null {
  if (rules.includes('light')) return 'light_required';
  if (rules.includes('pro')) return 'pro_required';
  return null;
}

/** Когда купить нечего: что делать дальше — честно и с адресом поддержки. */
const SUPPORT_HINT: Partial<Record<AccessDenialReason, string>> = {
  trial_expired: 'Этот материал открывался в пробный период и не входит в тарифы.',
  game_ownership_required: 'Материал открыт владельцам игры.',
  manual_grant_required: 'Доступ к материалу открывает администратор.',
  light_required: 'Оформление тарифа здесь пока не подключено.',
  pro_required: 'Оформление тарифа здесь пока не подключено.',
};

function SupportHint({ reason }: { reason: AccessDenialReason }) {
  const lead = SUPPORT_HINT[reason];
  if (!lead) return null;
  return (
    <Text size="sm" data-testid="locked-support">
      {lead} Чтобы получить доступ, напишите на{' '}
      <Anchor href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Anchor>.
    </Text>
  );
}

/**
 * Что делать с закрытым материалом (сценарии 19.10.2026): у замка должен быть
 * ключ, и только настоящий.
 *
 * - открывается тарифом — кнопка тарифа («Оформить», «Перейти на Про»);
 * - это игра и её владение продаётся — «Купить игру» на ту же страницу тарифов;
 * - купить нечего или оформление не подключено — к кому обратиться.
 *
 * Кнопки, которая ведёт туда, где материал не продаётся, нет: тариф не
 * предлагается, если материал им не открывается, игра — если её товара нет.
 */
export function LockedOffer({
  access: info,
  gameId,
  content,
}: {
  access: Denied;
  gameId?: string;
  /** Материал на странице: если он продаётся поштучно — кнопка «Купить». */
  content?: { kind: SellableContentKind; id: string };
}) {
  const { access } = useSession();
  const offer = useOffer();
  const [buying, setBuying] = useState<CheckoutFilter | null>(null);
  if (!access || info.reason === 'not_published' || info.reason === 'not_authenticated') return null;
  if (offer.isLoading) return null;
  const visible = Boolean(offer.data?.visible && offer.data.url);
  const tariff = visible ? tariffRoute(info.declaredRules) : null;
  const game = visible && gameId !== undefined && info.declaredRules.includes('game_owner') &&
    (offer.data?.purchasableGameIds ?? []).includes(gameId);
  const material = content !== undefined &&
    (offer.data?.purchasableContent ?? []).includes(`${content.kind}:${content.id}`);
  if (!tariff && !game && !material) return <SupportHint reason={info.reason} />;
  const direct = offer.data?.direct ?? false;
  const thisGame: CheckoutFilter = (product) => product.grantType === 'game_ownership' && product.gameId === gameId;
  const thisMaterial: CheckoutFilter = (product) =>
    product.grantType === 'content_access' && product.contentKind === content?.kind && product.contentId === content?.id;
  return (
    <Stack gap="xs">
      {tariff ? <PlanOfferButtons subscription={access.subscription} reason={tariff} /> : null}
      {material ? (
        <Group gap="xs" wrap="wrap">
          <Button
            size="xs"
            variant={tariff ? 'light' : 'filled'}
            onClick={() => setBuying(() => thisMaterial)}
            data-testid="buy-material"
          >
            {tariff ? 'Или купить только этот материал' : 'Купить доступ'}
          </Button>
          {!tariff && offer.data?.test ? (
            <Badge size="xs" variant="light" color="grape">{TEST_OFFER_BADGE}</Badge>
          ) : null}
        </Group>
      ) : null}
      {game ? (
        <Group gap="xs" wrap="wrap">
          <Button
            size="xs"
            variant={tariff ? 'light' : 'filled'}
            rightSection={direct ? undefined : <IconExternalLink size={13} />}
            onClick={() => {
              if (direct) setBuying(() => thisGame);
              else window.open(offer.data!.url!, '_blank', 'noopener');
            }}
            data-testid="buy-game"
          >
            Купить игру
          </Button>
          {!tariff && offer.data?.test ? (
            <Badge size="xs" variant="light" color="grape">{TEST_OFFER_BADGE}</Badge>
          ) : null}
        </Group>
      ) : null}
      <CheckoutModal filter={buying} onClose={() => setBuying(null)} />
    </Stack>
  );
}

/**
 * Под каталогом с закрытыми карточками — один «ключ» на все замки: если хотя
 * бы часть закрытого открывается тарифом, рядом кнопка тарифа. Кнопки на самих
 * карточках не ставятся: карточка ведёт на свою страницу, там — подробности.
 */
export function CatalogLockedOffer({ items }: { items: AccessInfo[] }) {
  const { access } = useSession();
  const denied = items.filter((item): item is Denied => !item.allowed && item.reason !== 'not_published');
  if (!access || denied.length === 0) return null;
  const routes = denied.map((item) => tariffRoute(item.declaredRules));
  const reason = routes.includes('light_required')
    ? 'light_required'
    : routes.includes('pro_required') ? 'pro_required' : null;
  return (
    <Paper withBorder p="md" mt="lg" radius="md" data-testid="catalog-offer" style={{ borderColor: 'var(--border-subtle)' }}>
      <Stack gap="xs">
        <Text size="sm">
          Закрыто здесь: {denied.length}.{' '}
          {reason
            ? reason === 'light_required'
              ? 'Открыть закрытое можно по тарифу Лайт или Про — подробности на странице материала.'
              : 'Открыть закрытое можно по тарифу Про — подробности на странице материала.'
            : 'Как открыть закрытое, написано на странице материала.'}
        </Text>
        {reason ? <PlanOfferButtons subscription={access.subscription} reason={reason} /> : null}
      </Stack>
    </Paper>
  );
}
